import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Container, Row, Col, Table, Spinner, Card, CardBody, CardHeader, Modal, ModalBody } from 'reactstrap';
import moment from 'moment';
import { Accommodation_Detail } from '../../api/AccommodationApi/AccommodationApi';
import { classes } from '../../data/layouts';

const AccommodationDetail = () => {
  const navigate = useNavigate();
  const { bookingId } = useParams();
  const defaultLayoutObj = classes.find((item) => Object.values(item).pop(1) === 'compact-wrapper');
  const layout = localStorage.getItem('layout') || Object.keys(defaultLayoutObj).pop();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [previewImg, setPreviewImg] = useState(null);

  const imgBaseURL = 'https://bytebiz.fra1.cdn.digitaloceanspaces.com/mcode/hisab-kitab/';

  const fetchBookingDetails = async () => {
    try {
      const { data } = await Accommodation_Detail(bookingId);
      setData(data?.payload?.data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookingDetails();
  }, []);

  if (loading)
    return (
      <div className="text-center mt-5" style={{ marginTop: '100px' }}>
        <Spinner color="primary" /> <p>Loading details...</p>
      </div>
    );

  if (!data) return <p>No details found.</p>;

  const { bookingDetail: booking, bookingPersons } = data;
  const person = booking.personDetail;

  return (
    <Container className="mt-4 ">
      {/* Back Button */}
      <button className="btn btn-secondary mb-3 " onClick={() => navigate(`/Accommodation/accommodation/${layout}`)}>
        ← Back
      </button>

      <h3 className="mb-4 text-center text-md-start">Accommodation Booking Details</h3>

      {/* ========= ROW 1 ========= */}
      <Row className="g-3">
        {/* Person Details */}
        <Col xs="12" md="6">
          <Card className="small-detail-card h-100">
            <CardHeader className="bg-primary text-white p-2">Person Details</CardHeader>
            <CardBody className="p-2">
              <Table size="sm" borderless responsive>
                <tbody>
                  <tr>
                    <th>Name</th>
                    <td>{person?.name}</td>
                  </tr>
                  <tr>
                    <th>Mobile</th>
                    <td>{person?.mobile}</td>
                  </tr>
                  <tr>
                    <th>Gender</th>
                    <td>{person?.gender}</td>
                  </tr>
                  <tr>
                    <th>Village</th>
                    <td>{person?.village}</td>
                  </tr>
                  <tr>
                    <th>DOB</th>
                    <td>{moment(person?.dob).format('DD MMM YYYY')}</td>
                  </tr>
                </tbody>
              </Table>

              <Row className="mt-3 g-2">
                <Col xs="6" className="text-center">
                  <img
                    src={imgBaseURL + person?.idProofFront}
                    alt="ID Front"
                    className="rounded img-fluid clickable-img"
                    onClick={() => setPreviewImg(imgBaseURL + person?.idProofFront)}
                    style={{ cursor: 'pointer' }}
                  />
                </Col>

                <Col xs="6" className="text-center">
                  <img
                    src={imgBaseURL + person?.idProofBack}
                    alt="ID Back"
                    className="rounded img-fluid clickable-img"
                    onClick={() => setPreviewImg(imgBaseURL + person?.idProofBack)}
                    style={{ cursor: 'pointer' }}
                  />
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>

        {/* Booking Details */}
        <Col xs="12" md="6">
          <Card className="small-detail-card h-100">
            <CardHeader className="bg-success text-white p-2">Booking Details</CardHeader>
            <CardBody className="p-2">
              <Table size="sm" borderless responsive>
                <tbody>
                  <tr>
                    <th>Booking ID</th>
                    <td>{booking?.bookingId}</td>
                  </tr>
                  <tr>
                    <th>Occupation</th>
                    <td>{booking?.occupation}</td>
                  </tr>
                  <tr>
                    <th>Check-In</th>
                    <td>{moment(booking?.checkInDateTime).format('DD MMM YYYY, hh:mm A')}</td>
                  </tr>
                  <tr>
                    <th>Check-Out</th>
                    <td>{moment(booking?.checkOutDateTime).format('DD MMM YYYY, hh:mm A')}</td>
                  </tr>
                  <tr>
                    <th>Persons</th>
                    <td>{booking?.noOfPerson}</td>
                  </tr>
                  <tr>
                    <th>Total Amount</th>
                    <td>
                      {booking?.currency} {booking?.amount}
                    </td>
                  </tr>
                  <tr>
                    <th>Status</th>
                    <td>
                      <span className={`badge ${booking?.status === 'paid' ? 'bg-success' : 'bg-danger'}`}>
                        {booking?.status}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <th>Order ID</th>
                    <td>{booking?.razorpay_order_id}</td>
                  </tr>
                  <tr>
                    <th>Payment ID</th>
                    <td>{booking?.razorpay_payment_id}</td>
                  </tr>
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* ========= Other Persons ========= */}
      <Card className="shadow-sm mt-4">
        <CardHeader className="bg-info text-white p-2">Other Persons</CardHeader>
        <CardBody className="p-2">
          <Table size="sm" bordered responsive>
            <thead className="table-light">
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Mobile</th>
                <th>Added On</th>
              </tr>
            </thead>
            <tbody>
              {bookingPersons?.length ? (
                bookingPersons.map((p, index) => (
                  <tr key={p.id}>
                    <td>{index + 1}</td>
                    <td>{p.name}</td>
                    <td>{p.mobile || 'N/A'}</td>
                    <td>{moment(p.createdAt).format('DD MMM YYYY, hh:mm A')}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center">
                    No additional persons
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </CardBody>
      </Card>

      {/* ========= IMAGE PREVIEW MODAL ========= */}
      <Modal isOpen={!!previewImg} toggle={() => setPreviewImg(null)} centered>
        <ModalBody className="text-center">
          <img src={previewImg} alt="Preview" className="img-fluid rounded" />
        </ModalBody>
      </Modal>
    </Container>
  );
};

export default AccommodationDetail;
